import React, { useState } from 'react';
import { API_BASE } from '../config';

function LocationSearch({ onLocationSelect, initialValue = '' }) {
  const [citySearch, setCitySearch] = useState(initialValue);
  const [cityResults, setCityResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const handleCitySearch = async (value) => {
    setCitySearch(value);
    setSearchError(null);

    if (value.length < 2) {
      setCityResults([]);
      setShowResults(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/geocode?city=${encodeURIComponent(value)}`);
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();

      if (!data.results || data.results.length === 0) {
        setSearchError('No locations found. Try searching for "Delhi, India" or "New Delhi, India".');
        setCityResults([]);
        setShowResults(true);
      } else {
        setCityResults(data.results);
        setShowResults(true);
        setSearchError(null);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError(`Unable to connect to location service. Is the backend running at ${API_BASE}? Error: ${error.message}`);
      setCityResults([]);
    }
  };

  const getTimezoneFromCoordinates = (lat, lon) => {
    // Precise mapping for common locations using IANA timezone database
    if (lat > 20 && lat < 35 && lon > 68 && lon < 97) return 'Asia/Kolkata';  // India
    if (lat > 34 && lat < 37 && lon > -118 && lon < -115) return 'America/Los_Angeles';  // California
    if (lat > 40 && lat < 42 && lon > -75 && lon < -73) return 'America/New_York';  // New York
    if (lat > 51 && lat < 52 && lon > -1 && lon < 1) return 'Europe/London';  // London
    if (lat > 48 && lat < 50 && lon > 1 && lon < 3) return 'Europe/Paris';  // Paris/Germany
    if (lat > 35 && lat < 37 && lon > 138 && lon < 141) return 'Asia/Tokyo';  // Japan
    if (lat > -34 && lat < -33 && lon > 150 && lon < 152) return 'Australia/Sydney';  // Sydney
    if (lat > 1 && lat < 2 && lon > 103 && lon < 104) return 'Asia/Singapore';  // Singapore

    // Fallback to offset-based IANA zones
    const tzOffset = Math.round(lon / 15);
    const tzMap = {
      '-8': 'America/Los_Angeles', '-7': 'America/Denver', '-6': 'America/Chicago',
      '-5': 'America/New_York', '-4': 'America/Antigua',
      '0': 'UTC', '1': 'Europe/Paris', '2': 'Europe/Athens', '3': 'Africa/Cairo',
      '5': 'Asia/Karachi', '5.5': 'Asia/Kolkata', '8': 'Asia/Singapore',
      '8.5': 'Australia/Adelaide', '9': 'Asia/Tokyo', '10': 'Australia/Sydney'
    };

    return tzMap[tzOffset] || 'UTC';
  };

  const selectCity = (result) => {
    // Prefer backend-provided timezone, fall back to coordinate-based calculation
    const timezone = result.timezone || getTimezoneFromCoordinates(result.latitude, result.longitude);

    // Extract city, state, country from the full name
    const parts = result.name.split(',').map(p => p.trim());
    const displayName = parts.length >= 3
      ? `${parts[0]}, ${parts[parts.length - 2]}, ${parts[parts.length - 1]}`
      : result.name;

    setCitySearch(displayName);
    setShowResults(false);

    // Call parent callback with location data
    onLocationSelect({
      city: parts[0],
      state: parts.length >= 2 ? parts[parts.length - 2] : '',
      country: parts.length >= 1 ? parts[parts.length - 1] : '',
      latitude: result.latitude,
      longitude: result.longitude,
      timezone: timezone,
      displayName: displayName
    });
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-white/90 mb-2">
        Birth Location (City, State, Country)
      </label>
      <input
        type="text"
        value={citySearch}
        onChange={(e) => handleCitySearch(e.target.value)}
        onFocus={() => cityResults.length > 0 && setShowResults(true)}
        placeholder="e.g., Delhi, Delhi, India"
        className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
      />

      {searchError && (
        <div className="mt-1 text-xs text-red-400">{searchError}</div>
      )}

      {showResults && cityResults.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-white/30 rounded max-h-48 overflow-y-auto z-10">
          {cityResults.map((result, idx) => {
            const parts = result.name.split(',').map(p => p.trim());
            const displayName = parts.length >= 3
              ? `${parts[0]}, ${parts[parts.length - 2]}, ${parts[parts.length - 1]}`
              : result.name;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => selectCity(result)}
                className="w-full text-left px-4 py-2 hover:bg-purple-500/50 text-white/90 text-sm border-b border-white/10 last:border-b-0 transition"
              >
                <div className="font-medium">{displayName}</div>
                <div className="text-white/60 text-xs">
                  Lat: {result.latitude.toFixed(4)}° | Lon: {result.longitude.toFixed(4)}°
                </div>
              </button>
            );
          })}
        </div>
      )}

      {citySearch && cityResults.length === 0 && showResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-white/30 rounded p-3 text-white/60 text-sm z-10">
          No locations found. Try a different search.
        </div>
      )}
    </div>
  );
}

export default LocationSearch;
